import React, { useState, useEffect } from 'react';
import { 
  X, 
  MapPin, 
  Search, 
  TrendingUp, 
  DollarSign, 
  Users,
  Building2,
  ChevronRight,
  Info
} from 'lucide-react';
import axiosInstance from '../../../services/axiosInstance';
import DataToolTip from '../../common/DataToolTip';
import './styles.scss';

const RegionalOccupationModal = ({ isOpen, onClose, areaName, datasetType, year }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen && areaName) {
      fetchRegionalData();
    }
  }, [isOpen, areaName, datasetType, year]);

  const fetchRegionalData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/datasets/${datasetType}/analytics?year=${year}&areaName=${encodeURIComponent(areaName)}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching regional occupations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const filteredRegistry = (data?.registry || []).filter(occ => 
    occ.occTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    occ.occCode?.includes(searchTerm)
  );

  return (
    <div className="modal-overlay-glass" onClick={onClose}>
      <div 
        className="regional-occupation-modal-premium animate-scale-in" 
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-header">
          <div className="header-content">
            <div className="icon-badge icon-badge--primary">
              <MapPin size={24} />
            </div>
            <div className="title-stack">
              <span className="region-badge">{datasetType?.toUpperCase()} Collection</span>
              <h2>{areaName}</h2>
              <p className="subtitle">Comprehensive Workforce Inventory • {year}</p>
            </div>
          </div>
          <button className="close-btn-round" onClick={onClose}>
            <X size={20} />
          </button>
        </header>

        <div className="modal-body-scrollable">
          {/* Summary Stats Bar */}
          <section className="regional-stats-bar">
            <DataToolTip text="Total aggregated employment across all documented occupations within this regional jurisdiction." direction="down">
              <div className="stat-pill">
                <Users size={16} />
                <div className="stat-content">
                  <label>Total Jobs</label>
                  <strong>{Number(data?.totalRecords || 0).toLocaleString()}</strong>
                </div>
              </div>
            </DataToolTip>
            
            <DataToolTip text="The calculated median annual wage across the entire regional workforce inventory." direction="down">
              <div className="stat-pill">
                <TrendingUp size={16} />
                <div className="stat-content">
                  <label>Avg Median</label>
                  <strong>${Number(data?.medianWage || 0).toLocaleString()}</strong>
                </div>
              </div>
            </DataToolTip>

            <DataToolTip text="Number of unique industry sectors (NAICS codes) represented in this regional dataset." direction="down">
              <div className="stat-pill">
                <Building2 size={16} />
                <div className="stat-content">
                  <label>Sectors</label>
                  <strong>{Math.max(data?.naicsBreakdown?.length || 0, data?.uniqueNaics || 0)}</strong>
                </div>
              </div>
            </DataToolTip>
          </section>

          {/* Sector Breakdown (New) */}
          {(data?.naicsBreakdown || []).length > 0 && (
            <section className="regional-sectors-list">
              <div className="section-header">
                <h3><Building2 size={16} /> Regional Industry Distribution</h3>
                <p>Top industries contributing to the workforce in {areaName}.</p>
              </div>
              <div className="sector-pills-container">
                {data.naicsBreakdown.map((sector, idx) => (
                  <div key={idx} className="sector-item-pill">
                    <span className="sector-name">{sector.name}</span>
                    <span className="sector-value">{sector.value?.toLocaleString()} Jobs</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Search Controls */}
          <div className="registry-controls">
            <div className="search-wrapper">
              <Search size={18} />
              <input 
                type="text" 
                placeholder="Search occupations by title or SOC code..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="premium-loader"></div>
              <p>Aggregating regional workforce data...</p>
            </div>
          ) : (
            <div className="registry-table-wrapper">
              <table className="premium-registry-table">
                <thead>
                  <tr>
                    <th>SOC Code</th>
                    <th>Occupation Title</th>
                    <th className="text-right">Employment</th>
                    <th className="text-right">Median Wage</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRegistry.length > 0 ? (
                    filteredRegistry.map((occ, idx) => (
                      <tr key={idx}>
                        <td className="soc-code-cell">
                          <span className="code-badge">{occ.occCode}</span>
                        </td>
                        <td className="title-cell">
                          <strong>{occ.occTitle}</strong>
                        </td>
                        <td className="text-right font-mono">
                          {Number(occ.totalEmployment || 0).toLocaleString()}
                        </td>
                        <td className="text-right font-mono text-emerald">
                          ${Number(occ.annualMedian || occ.annualMedianWage || 0).toLocaleString()}
                        </td>
                        <td className="action-cell">
                          <ChevronRight size={16} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="empty-row">
                        No occupations found matching "{searchTerm}" in this region.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <footer className="modal-footer">
          <div className="footer-info">
            Showing {filteredRegistry.length} of {data?.registry?.length || 0} occupations
          </div>
          <button className="btn-secondary" onClick={onClose}>
            Close Explorer
          </button>
        </footer>
      </div>
    </div>
  );
};

export default RegionalOccupationModal;
